import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { rmsApi } from '../../../state/axios';
import type { RootState } from '../../../state/store';
import { buildExportTimeRangeParam } from '../helpers/buildExportTimeRange';
import type { ExportDateRange, QuotaData, QuotaModalState, ReportType } from '../types';

// Hook for managing export quota state and API calls
export const useExportQuota = () => {
  const routeMatch = useParams();
  const companyDetails = useSelector((state: RootState) => state.company) as any;
  const { filters } = useSelector((state: RootState) => state);
  const { dates, timeRange, locations: locationFilters } = filters;

  const [quotaModal, setQuotaModal] = useState<QuotaModalState>({
    show: false,
    loading: false,
    data: null,
    reportType: null
  });

  const checkExportQuota = async (
    reportVariation: ReportType,
    exportDateRange?: ExportDateRange
  ): Promise<QuotaData> => {
    try {
      // eslint-disable-next-line max-len
      const quotaUrl = `/api/v1/dar/${routeMatch.dashboard}/${routeMatch.widget}/report/download/${reportVariation.url}/quota`;

      // Prepare query parameters including time range for duplicate detection
      const params: any = {};

      const dateRangeToUse: ExportDateRange = exportDateRange || {
        dates: dates as [string, string],
        timeRange: timeRange as [[number, number], [number, number]]
      };

      params.time_range = buildExportTimeRangeParam(
        dateRangeToUse,
        companyDetails?.company_details?.time_zone_offset || '+00:00'
      );

      if (companyDetails?.company_details?.company_id) {
        params.company_id = companyDetails.company_details.company_id.toString();
      }

      if (locationFilters && locationFilters.length > 0) {
        params.location_ids = locationFilters.join(',');
      }

      if (companyDetails?.company_details?.currency_code) {
        params.currency_code = companyDetails.company_details.currency_code;
      }

      const response = await rmsApi.get(quotaUrl, { params });
      return response.data;
    } catch (error) {
      console.error('Quota check error:', error);
      throw error;
    }
  };

  const showQuotaModal = (reportType: ReportType) => {
    setQuotaModal({
      show: true,
      loading: true,
      data: null,
      reportType
    });
  };

  const setQuotaData = (data: QuotaData) => {
    setQuotaModal((prev) => ({
      ...prev,
      loading: false,
      data
    }));
  };

  const hideQuotaModal = () => {
    setQuotaModal({
      show: false,
      loading: false,
      data: null,
      reportType: null,
      exportProcessing: false,
      exportCompleted: false
    });
  };

  const setQuotaError = () => {
    setQuotaModal({
      show: false,
      loading: false,
      data: null,
      reportType: null,
      exportProcessing: false,
      exportCompleted: false
    });
  };

  const setExportProcessing = () => {
    setQuotaModal((prev) => ({
      ...prev,
      exportProcessing: true,
      exportCompleted: false
    }));
  };

  const setExportCompleted = () => {
    setQuotaModal((prev) => ({
      ...prev,
      exportProcessing: false,
      exportCompleted: true
    }));
  };

  return {
    quotaModal,
    checkExportQuota,
    showQuotaModal,
    setQuotaData,
    hideQuotaModal,
    setQuotaError,
    setExportProcessing,
    setExportCompleted,
    setQuotaModal
  };
};
